import { type ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import type React from 'react';
import { type ReactNode, useMemo } from 'react';
import {
  createContext, useCallback,
  useContext, useState,
} from 'react';

type BreadcrumbItem = ItemType & { path: string; order: number };

type BreadcrumbContextType = {
  items: BreadcrumbItem[];
  addBreadcrumbForRoute: (item: BreadcrumbItem) => void;
  removeBreadcrumbForRoute: (path: string) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export const BreadcrumbProvider: React.FC<{ children: ReactNode }> = function BreadcrumbProvider({ children }) {
  const [breadcrumbMap, setBreadcrumbMap] = useState<Map<string, BreadcrumbItem>>(new Map());

  const addBreadcrumbForRoute = useCallback((item: Omit<BreadcrumbItem, 'key'>) => {
    setBreadcrumbMap((prevMap) => new Map(prevMap).set(item.path, { ...item }));
  }, []);

  const removeBreadcrumbForRoute = useCallback((path: string) => {
    setBreadcrumbMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.delete(path);
      return newMap;
    });
  }, []);

  const items = useMemo(() => Array.from(breadcrumbMap.values())
    .sort((a, b) => a.order - b.order), [breadcrumbMap]);

  const contextValue = useMemo(() => ({
    items,
    addBreadcrumbForRoute,
    removeBreadcrumbForRoute,
  }), [items, addBreadcrumbForRoute, removeBreadcrumbForRoute]);

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = () => {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
};
